(() => {
  const ROOT_SELECTOR = '[data-ch-wholesale-registration]';
  const FIELDS = ['name', 'email', 'phone', 'website', 'addressLine1', 'addressLine2', 'city', 'state', 'postalCode', 'country'];

  const escapeHTML = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const compact = (values) => values.map((value) => String(value || '').trim()).filter(Boolean);

  const formatAddress = (store) => compact([
    store.addressLine1,
    store.addressLine2,
    compact([store.city, store.state, store.postalCode]).join(', '),
    store.country,
  ]).join('<br>');

  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = String(url).trim();
    if (!trimmed) return '';
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  class WholesaleRegistration {
    constructor(root) {
      this.root = root;
      this.proxyBase = (root.dataset.proxyBase || '/apps/wholesale-locator').replace(/\/$/, '');
      this.shopDomain = root.dataset.shopDomain || '';
      this.isCustomer = root.dataset.isCustomer === 'true';
      this.messages = {
        loading: root.dataset.loadingText || 'Loading your store locations...',
        empty: root.dataset.emptyStoresText || 'You have not submitted any store locations yet.',
        createSuccess: root.dataset.createSuccessText || 'Thanks. Your store has been submitted for review.',
        updateSuccess: root.dataset.updateSuccessText || 'Your store update has been submitted for review.',
        deleteSuccess: root.dataset.deleteSuccessText || 'The store location was removed.',
        error: root.dataset.errorText || 'Something went wrong. Please check the form and try again.',
        stockError: root.dataset.stockErrorText || 'Select at least one product type.',
      };

      this.form = root.querySelector('[data-registration-form]');
      this.formTitle = root.querySelector('[data-registration-form-title]');
      this.editingInput = root.querySelector('[data-editing-id]');
      this.submitButton = root.querySelector('[data-submit-label]');
      this.cancelButton = root.querySelector('[data-cancel-edit]');
      this.status = root.querySelector('[data-registration-status]');
      this.stocksError = root.querySelector('[data-stocks-error]');
      this.storesPanel = root.querySelector('[data-customer-stores-panel]');
      this.storesStatus = root.querySelector('[data-stores-status]');
      this.storesList = root.querySelector('[data-stores-list]');
      this.stores = [];
    }

    init() {
      this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
      this.cancelButton?.addEventListener('click', () => this.resetForm());

      if (this.isCustomer) {
        this.loadStores();
      }
    }

    async loadStores() {
      this.setStoresStatus(this.messages.loading);

      try {
        const response = await fetch(this.proxyUrl('/stores'), { headers: { Accept: 'application/json' } });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false || data.ok === false) throw new Error(data.error || 'Unable to load stores');
        this.stores = Array.isArray(data.stores) ? data.stores : [];
        this.renderStores();
      } catch (error) {
        console.error(error);
        this.setStoresStatus(this.messages.error, 'error');
      }
    }

    renderStores() {
      if (!this.storesList) return;

      if (!this.stores.length) {
        this.storesList.innerHTML = '';
        this.setStoresStatus(this.messages.empty);
        return;
      }

      this.setStoresStatus(`${this.stores.length} ${this.stores.length === 1 ? 'location' : 'locations'} found.`);
      this.storesList.innerHTML = this.stores.map((store) => this.storeCardTemplate(store)).join('');

      this.storesList.querySelectorAll('[data-edit-store]').forEach((button) => {
        button.addEventListener('click', () => this.startEdit(button.dataset.editStore));
      });

      this.storesList.querySelectorAll('[data-delete-store]').forEach((button) => {
        button.addEventListener('click', () => this.deleteStore(button.dataset.deleteStore));
      });
    }

    storeCardTemplate(store) {
      const url = normalizeUrl(store.website);
      const contact = compact([
        store.email ? `<a href="mailto:${escapeHTML(store.email)}">Email</a>` : '',
        url ? `<a href="${escapeHTML(url)}" target="_blank" rel="noopener noreferrer">Website</a>` : '',
      ]).join(' · ');
      const stocks = Array.isArray(store.stocks) ? store.stocks.join(', ') : '';
      const status = store.verified ? 'Approved' : 'In review';

      return `
        <li class="ch-wholesale-registration__store-card">
          <h3>${escapeHTML(store.name)}</h3>
          <p>${formatAddress(store)}</p>
          ${contact ? `<p>${contact}</p>` : ''}
          ${stocks ? `<p><strong>Stocks:</strong> ${escapeHTML(stocks)}</p>` : ''}
          <span class="ch-wholesale-registration__badge">${status}</span>
          <div class="ch-wholesale-registration__store-actions">
            <button class="ch-wholesale-registration__store-action" type="button" data-edit-store="${escapeHTML(store.id)}">Edit</button>
            <button class="ch-wholesale-registration__store-action ch-wholesale-registration__store-action--delete" type="button" data-delete-store="${escapeHTML(store.id)}">Delete</button>
          </div>
        </li>
      `;
    }

    async handleSubmit(event) {
      event.preventDefault();
      this.clearStatus();

      if (!this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      const payload = this.formPayload();
      if (!payload.stocks.length) {
        this.stocksError.hidden = false;
        this.stocksError.textContent = this.messages.stockError;
        return;
      }

      this.stocksError.hidden = true;
      const editingId = this.editingInput?.value || '';
      const isEditing = Boolean(editingId);
      const url = isEditing ? this.proxyUrl(`/stores/${encodeURIComponent(editingId)}`) : this.proxyUrl('/submissions');
      const method = isEditing ? 'PUT' : 'POST';

      this.setBusy(true);

      try {
        const response = await fetch(url, {
          method,
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false || data.ok === false) throw new Error(data.error || 'Unable to submit store');

        this.setStatus(isEditing ? this.messages.updateSuccess : this.messages.createSuccess, 'success');
        this.resetForm();
        if (this.isCustomer) await this.loadStores();
      } catch (error) {
        console.error(error);
        this.setStatus(error.message || this.messages.error, 'error');
      } finally {
        this.setBusy(false);
      }
    }

    formPayload() {
      const formData = new FormData(this.form);
      const payload = {};
      FIELDS.forEach((field) => {
        payload[field] = String(formData.get(field) || '').trim();
      });
      payload.stocks = formData.getAll('stocks').map((stock) => String(stock).trim()).filter(Boolean);
      return payload;
    }

    startEdit(id) {
      const store = this.stores.find((item) => String(item.id) === String(id));
      if (!store) return;

      FIELDS.forEach((field) => {
        const input = this.form.elements[field];
        if (input) input.value = store[field] || '';
      });

      const stocks = new Set(Array.isArray(store.stocks) ? store.stocks : []);
      this.form.querySelectorAll('input[name="stocks"]').forEach((input) => {
        input.checked = stocks.has(input.value);
      });

      this.editingInput.value = store.id;
      this.submitButton.textContent = this.submitButton.dataset.updateLabel || 'Update store';
      this.cancelButton.hidden = false;
      if (this.formTitle) this.formTitle.textContent = 'Edit store details';
      this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    async deleteStore(id) {
      const store = this.stores.find((item) => String(item.id) === String(id));
      const name = store?.name || 'this store';
      if (!window.confirm(`Delete ${name}?`)) return;

      this.setStoresStatus('Deleting store...');

      try {
        const response = await fetch(this.proxyUrl(`/stores/${encodeURIComponent(id)}`), {
          method: 'DELETE',
          headers: { Accept: 'application/json' },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false || data.ok === false) throw new Error(data.error || 'Unable to delete store');
        this.setStatus(this.messages.deleteSuccess, 'success');
        await this.loadStores();
      } catch (error) {
        console.error(error);
        this.setStoresStatus(error.message || this.messages.error, 'error');
      }
    }

    resetForm() {
      const submitLabel = this.submitButton?.dataset.submitLabel || 'Submit application';
      this.form.reset();
      if (this.editingInput) this.editingInput.value = '';
      if (this.submitButton) this.submitButton.textContent = submitLabel;
      if (this.cancelButton) this.cancelButton.hidden = true;
      if (this.formTitle) this.formTitle.textContent = this.formTitle.dataset.defaultTitle || 'Store details';
      if (this.stocksError) this.stocksError.hidden = true;
    }

    setBusy(isBusy) {
      this.submitButton.disabled = isBusy;
      this.submitButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    setStatus(message, kind = '') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', kind === 'error');
      this.status.classList.toggle('is-success', kind === 'success');
    }

    setStoresStatus(message, kind = '') {
      if (!this.storesStatus) return;
      this.storesStatus.textContent = message;
      this.storesStatus.classList.toggle('is-error', kind === 'error');
      this.storesStatus.classList.toggle('is-success', kind === 'success');
    }

    clearStatus() {
      this.setStatus('');
      this.setStoresStatus(this.storesStatus?.textContent || '');
    }

    proxyUrl(path) {
      const url = new URL(`${this.proxyBase}${path}`, window.location.origin);
      const isLocalPreview = ['127.0.0.1', 'localhost'].includes(window.location.hostname);
      if (isLocalPreview && this.shopDomain && !url.searchParams.has('shop')) {
        url.searchParams.set('shop', this.shopDomain);
      }
      return url.toString();
    }
  }

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.chWholesaleRegistrationReady === 'true') return;
      root.dataset.chWholesaleRegistrationReady = 'true';
      new WholesaleRegistration(root).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
